<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseContactPhoneNumber } from 'shared/helpers/phoneNumberHelper';
import {
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ContactAPI from 'dashboard/api/contacts';
import camelcaseKeys from 'camelcase-keys';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  phoneNumber: { type: String, required: true },
});

const emit = defineEmits(['close']);

const i18nPrefix = 'CONVERSATION.PHONE_NUMBER_DIALOG';
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { fetchSignatureFlagFromUISettings } = useUISettings();

const dialogRef = ref(null);
const contact = ref(null);
const targetInbox = ref(null);
const isResolving = ref(true);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isComposing = ref(false);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const inboxesList = useMapGetter('inboxes/getInboxes');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const conversationsUiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const messageSignaturePosition = useMapGetter('getMessageSignaturePosition');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const digits = computed(() => props.phoneNumber.replace(/\D/g, ''));

const formattedNumber = computed(
  () =>
    parseContactPhoneNumber(props.phoneNumber)?.formatInternational() ||
    props.phoneNumber
);

const contactPath = id =>
  frontendURL(`accounts/${accountId.value}/contacts/${id}`);

// The search matches on a substring, so the results are narrowed down to the
// contact that actually owns this number.
onMounted(async () => {
  dialogRef.value?.open();
  try {
    const {
      data: { payload },
    } = await ContactAPI.search(digits.value);
    const match = payload.find(
      ({ phone_number: number }) => number?.replace(/\D/g, '') === digits.value
    );
    // The compose form reads camelCased contacts, the same shape createNewContact returns.
    contact.value = match ? camelcaseKeys(match, { deep: true }) : null;
  } catch (error) {
    // The number is then offered for creation, and a duplicate is rejected by the API.
    contact.value = null;
  } finally {
    isResolving.value = false;
  }
});

const close = () => emit('close');

const openContact = id => {
  router.push(contactPath(id));
  close();
};

// The compose form lists the channels this contact can be reached on, so its
// inboxes are resolved before the form is shown.
const startComposing = async () => {
  isComposing.value = true;
  isFetchingInboxes.value = true;
  try {
    const contactableInboxes = await fetchContactableInboxes(contact.value.id);
    contact.value.contactInboxes = mergeInboxDetails(
      contactableInboxes,
      inboxesList.value
    );
  } finally {
    isFetchingInboxes.value = false;
  }
};

const createContact = async () => {
  isCreatingContact.value = true;
  try {
    contact.value = await createNewContact(props.phoneNumber);
    return true;
  } catch (error) {
    const message = parseAPIErrorResponse(error);
    useAlert(
      typeof message === 'string' ? message : t(`${i18nPrefix}.CREATE_ERROR`)
    );
    return false;
  } finally {
    isCreatingContact.value = false;
  }
};

const handleCreateAndSend = async () => {
  if (await createContact()) startComposing();
};

const handleCreateOnly = async () => {
  if (!(await createContact())) return;
  useAlert(t(`${i18nPrefix}.CONTACT_CREATED`), {
    type: 'link',
    to: contactPath(contact.value.id),
    message: t(`${i18nPrefix}.VIEW_CONTACT`),
  });
  close();
};

const handleCopy = async () => {
  await copyTextToClipboard(props.phoneNumber);
  useAlert(t(`${i18nPrefix}.COPIED`));
  close();
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    router.push(
      frontendURL(`accounts/${data.account_id}/conversations/${data.id}`)
    );
    close();
  } catch (error) {
    useAlert(t(`${i18nPrefix}.SEND_ERROR`));
  }
};
</script>

<template>
  <Dialog
    v-if="!isComposing"
    ref="dialogRef"
    width="sm"
    :title="formattedNumber"
    :description="
      isResolving ? '' : contact ? contact.name : t(`${i18nPrefix}.NO_CONTACT`)
    "
    :show-confirm-button="false"
    :cancel-button-label="t(`${i18nPrefix}.CLOSE`)"
    @close="close"
  >
    <div
      v-if="isResolving || isCreatingContact"
      class="flex items-center justify-center py-4"
    >
      <Spinner class="size-5 text-n-slate-11" />
    </div>
    <div v-else class="flex flex-col gap-2">
      <template v-if="contact">
        <Button
          type="button"
          variant="faded"
          color="slate"
          justify="start"
          icon="i-lucide-user"
          :label="t(`${i18nPrefix}.OPEN_CONTACT`)"
          @click="openContact(contact.id)"
        />
        <Button
          type="button"
          variant="faded"
          color="slate"
          justify="start"
          icon="i-lucide-send"
          :label="t(`${i18nPrefix}.SEND_MESSAGE`)"
          @click="startComposing"
        />
      </template>
      <template v-else>
        <Button
          type="button"
          variant="faded"
          color="slate"
          justify="start"
          icon="i-lucide-user-plus"
          :label="t(`${i18nPrefix}.CREATE_AND_SEND`)"
          @click="handleCreateAndSend"
        />
        <Button
          type="button"
          variant="faded"
          color="slate"
          justify="start"
          icon="i-lucide-user-round-plus"
          :label="t(`${i18nPrefix}.CREATE_ONLY`)"
          @click="handleCreateOnly"
        />
      </template>
      <Button
        type="button"
        variant="faded"
        color="slate"
        justify="start"
        icon="i-lucide-clipboard"
        :label="t(`${i18nPrefix}.COPY`)"
        @click="handleCopy"
      />
    </div>
  </Dialog>

  <!-- The compose form is a self-contained panel and carries its own buttons, so
       it gets a plain overlay instead of the Dialog's form wrapper, whose submit
       would fire on every button inside it. -->
  <TeleportWithDirection v-else to="body">
    <!-- Closing on the backdrop itself, rather than on any click outside the
         form, keeps the emoji and attachment popups from dismissing it. -->
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
      @click.self="close"
    >
      <ComposeNewConversationForm
        :form-state="formState"
        :contacts="[]"
        :contact-id="String(contact.id)"
        :is-loading="false"
        :current-user="currentUser"
        :selected-contact="contact"
        :target-inbox="targetInbox"
        :is-creating-contact="false"
        :is-fetching-inboxes="isFetchingInboxes"
        :is-direct-uploads-enabled="globalConfig.directUploadsEnabled"
        :contact-conversations-ui-flags="conversationsUiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :message-signature-position="messageSignaturePosition"
        :send-with-signature="sendWithSignature"
        @update-target-inbox="targetInbox = $event"
        @create-conversation="createConversation"
        @discard="close"
      />
    </div>
  </TeleportWithDirection>
</template>
